import React from 'react';
import { PublicLayout } from '../../components/PublicLayout';
import { PageHero, BenefitsGrid, FAQSection, CTASection, FeatureList } from '../../components/PageTemplate';
import { 
  Shield, FileText, AlertTriangle, Clock, CheckCircle, 
  Bell, MapPin, Lock, BarChart 
} from 'lucide-react';

export default function LandlordsCompliance() {
  const requirements = [
    {
      title: 'England',
      items: [
        'Energy Performance Certificate (EPC) - E rating minimum',
        'Gas Safety Certificate (CP12) - Annual',
        'Electrical Installation Condition Report (EICR) - Every 5 years',
        'How to Rent Guide - Latest version to tenants',
        'Deposit Protection - Within 30 days',
        'Smoke & CO Alarms - Working on every floor'
      ]
    },
    {
      title: 'Scotland',
      items: [
        'Landlord Registration - Required before letting',
        'Energy Performance Certificate (EPC)',
        'Electrical Installation Condition Report (EICR)',
        'Gas Safety Certificate (CP12) - Annual',
        'Legionella Risk Assessment',
        'Deposit Protection - Within 30 working days'
      ]
    },
    {
      title: 'Wales',
      items: [
        'Rent Smart Wales Registration - Landlord & Agent',
        'Energy Performance Certificate (EPC)',
        'Electrical Installation Condition Report (EICR)',
        'Gas Safety Certificate (CP12) - Annual',
        'Fitness for Human Habitation',
        'Smoke & CO Alarms'
      ]
    }
  ];

  const benefits = [
    {
      icon: Bell,
      title: 'Expiry Reminders',
      description: 'We track every certificate and send reminders 30, 14, and 7 days before expiry.'
    },
    {
      icon: Shield,
      title: 'Compliance Gating',
      description: 'Properties won\'t go live until all required certificates are uploaded and valid.'
    },
    {
      icon: MapPin,
      title: 'Jurisdiction Detection',
      description: 'We automatically detect your property\'s jurisdiction and apply the right rules.'
    },
    {
      icon: FileText,
      title: 'Document Storage',
      description: 'All certificates stored securely, accessible anytime, shared with tenants automatically.'
    },
    {
      icon: Clock,
      title: 'Audit Trail',
      description: 'Complete history of when documents were uploaded, viewed, and updated.'
    },
    {
      icon: BarChart,
      title: 'Compliance Dashboard',
      description: 'See the status of all your properties at a glance - green, amber, or red.'
    }
  ];

  const faqs = [
    {
      question: 'What happens if a certificate expires?',
      answer: 'We send multiple reminders before expiry. If a certificate expires, your listing is automatically paused until you upload a valid replacement. This protects you legally.'
    },
    {
      question: 'Can you arrange compliance certificates for me?',
      answer: 'Yes - we offer EPC, Gas Safety, and EICR as add-on services. We\'ll arrange for a qualified professional to visit your property.'
    },
    {
      question: 'How do I prove compliance to tenants?',
      answer: 'Tenants automatically receive copies of relevant certificates through their portal. You don\'t need to do anything.'
    },
    {
      question: 'What if my property is an HMO?',
      answer: 'We detect HMO properties and apply additional requirements including HMO licensing, fire safety, and room size regulations.'
    }
  ];

  return (
    <PublicLayout>
      <PageHero
        badge="COMPLIANCE & SAFETY"
        title="Stay Compliant,"
        subtitle="Automatically"
        description="We track every certificate, remind you before deadlines, and won\'t let you list without proper documentation. Peace of mind built in."
        primaryCTA={{ href: '/register', label: 'Get Started' }}
        secondaryCTA={{ href: '/landlords', label: 'Back to Overview' }}
        gradient="from-amber-50 via-white to-slate-50"
      />

      <BenefitsGrid title="How We Keep You Compliant" benefits={benefits} />

      {/* Requirements by Jurisdiction */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-center mb-4">
            Requirements by Jurisdiction
          </h2>
          <p className="text-center text-slate-600 mb-8 sm:mb-12 max-w-2xl mx-auto">
            Different rules apply in England, Scotland, and Wales. We handle this automatically.
          </p>
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            {requirements.map((region) => (
              <div key={region.title} className="bg-slate-50 rounded-xl p-5 sm:p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  {region.title}
                </h3>
                <FeatureList features={region.items} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />

      <CTASection
        title="Never miss a compliance deadline"
        description="Let LetWise track your certificates and keep you protected."
        primaryCTA={{ href: '/register', label: 'Get Started Free' }}
      />
    </PublicLayout>
  );
}
